import { useState, useEffect } from "react";
import {
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useQueryMutation from "hooks/useQueryMutation";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useFetch from "hooks/useFetch";

export default function MutateGroupMenu() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: deleteGroup, isPending: isDeleting } = useQueryMutation(
    `/customer/${customerId}`,
    "DELETE"
  );
  const { mutate: updateGroup, isPending: isUpdating } = useQueryMutation(
    `/customer/${customerId}`,
    "PATCH"
  );
  const { data: customer } = useFetch(`/customer/${customerId}`);
  const { data: customerProjects } = useFetch(
    `/project`,
    {
      params: { filter_customer_id: customerId },
    },
    !!customerId
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [action, setAction] = useState("");
  const [name, setName] = useState("");

  const menuOpen = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const closeDialog = () => setAction("");

  useEffect(() => {
    setName(customer?.name || "");
  }, [customer]);

  const handleDelete = () => {
    deleteGroup(
      {},
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["/customer"] });
          closeDialog();
          navigate("/");
        },
      }
    );
  };

  const handleUpdate = () => {
    updateGroup(
      { name },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["/customer"],
          });
          closeDialog();
        },
      }
    );
  };

  const notDeletable = customerProjects?.count > 0;
  const disabled = isUpdating || !name.trim();

  return (
    <>
      <Tooltip title="Group settings">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
          aria-controls={menuOpen ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id="project-menu"
        open={menuOpen}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => setAction("edit")}>Edit Group</MenuItem>
        <MenuItem onClick={() => setAction("delete")}>Delete Group</MenuItem>
      </Menu>

      <Dialog
        open={action === "delete"}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {notDeletable
              ? "You cannot delete a group that contains projects. Move the projects, and delete the group."
              : "Are you sure you want to delete this group?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {notDeletable ? (
            <Button onClick={closeDialog}>OK</Button>
          ) : (
            <>
              <Button onClick={closeDialog}>Cancel</Button>
              <Button onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={action === "edit"}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Group Name"
            type="text"
            fullWidth
            variant="standard"
            value={name}
            required
            maxLength={32}
            disabled={disabled}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={disabled}>
            {isUpdating ? "Updating..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
